/**
 * Shared TikZ shape catalog for the Insert → Shapes ribbon picker.
 * Each entry provides searchable metadata and the body drawn inside a tikzpicture.
 */

export type TikzShapeId =
  | "circle"
  | "rectangle"
  | "line"
  | "ellipse"
  | "arc"
  | "bezier"
  | "diamond"
  | "trapezium"
  | "semicircle"
  | "cylinder"

export type TikzShape = {
  id: TikzShapeId
  name: string
  /** Extra terms matched by the picker search box */
  keywords: string[]
  /** TikZ libraries required beyond the base `tikz` package */
  libraries?: string[]
  /** Draw commands (no surrounding `\begin{tikzpicture}` …) */
  body: string
  /** Simple SVG path/preview used in the picker card */
  preview: "circle" | "rect" | "line" | "ellipse" | "arc" | "bezier" | "diamond" | "trapezium" | "semicircle" | "cylinder"
}

const GRID =
  "  \\draw[thin, gray, very thin] (-4,-4) grid (4,4);"

export const TIKZ_SHAPES: TikzShape[] = [
  {
    id: "circle",
    name: "Circle",
    keywords: ["round", "oval", "dot"],
    body: "  \\node[circle, draw, minimum size=1.5cm] {};",
    preview: "circle",
  },
  {
    id: "rectangle",
    name: "Rectangle",
    keywords: ["box", "square", "rect"],
    body: "  \\draw (0,0) rectangle (2,1);",
    preview: "rect",
  },
  {
    id: "line",
    name: "Line",
    keywords: ["segment", "stroke", "path"],
    body: `${GRID}\n  \\draw (0,0) -- (0,-2.5);`,
    preview: "line",
  },
  {
    id: "ellipse",
    name: "Ellipse",
    keywords: ["oval", "egg"],
    body: `${GRID}\n  \\draw (0,0) ellipse (2cm and 1cm);`,
    preview: "ellipse",
  },
  {
    id: "arc",
    name: "Arc",
    keywords: ["curve", "partial", "circle"],
    body: `${GRID}\n  \\draw (0,0) arc (0:90:2.5);`,
    preview: "arc",
  },
  {
    id: "bezier",
    name: "Bezier",
    keywords: ["curve", "spline", "controls", "wave"],
    body: `${GRID}\n  \\draw (0,0) .. controls (1,1.5) and (2.5,-1) .. (4,0);`,
    preview: "bezier",
  },
  {
    id: "diamond",
    name: "Diamond",
    keywords: ["rhombus", "kite", "lozenge"],
    libraries: ["shapes.geometric"],
    body: `${GRID}\n  \\node[diamond, draw, minimum size=1.5cm] {};`,
    preview: "diamond",
  },
  {
    id: "trapezium",
    name: "Trapezium",
    keywords: ["trapezoid", "quad"],
    libraries: ["shapes.geometric"],
    body: `${GRID}\n  \\node[trapezium, draw, minimum width=2cm, minimum height=1cm] {};`,
    preview: "trapezium",
  },
  {
    id: "semicircle",
    name: "Semicircle",
    keywords: ["half", "circle", "dome"],
    libraries: ["shapes.geometric"],
    body: `${GRID}\n  \\node[semicircle, draw, minimum size=1.5cm] {};`,
    preview: "semicircle",
  },
  {
    id: "cylinder",
    name: "Cylinder",
    keywords: ["tube", "can", "3d"],
    libraries: ["shapes.geometric"],
    body: `${GRID}\n  \\node[cylinder, draw, aspect=0.5, minimum height=2cm, minimum width=1cm] {};`,
    preview: "cylinder",
  },
]

export function getTikzShape(id: string): TikzShape | undefined {
  return TIKZ_SHAPES.find((s) => s.id === id)
}

export function searchTikzShapes(query: string): TikzShape[] {
  const q = query.trim().toLowerCase()
  if (!q) return TIKZ_SHAPES
  return TIKZ_SHAPES.filter((s) => {
    if (s.name.toLowerCase().includes(q) || s.id.includes(q)) return true
    return s.keywords.some((k) => k.toLowerCase().includes(q))
  })
}

/** Full insertable snippet for a shape (tikzpicture wrapper). */
export function tikzShapeSnippet(shape: TikzShape): string {
  return `\\begin{tikzpicture}\n${shape.body}\n\\end{tikzpicture}`
}
